// ─────────────────────────────────────────────────────────────────────────────
// ESG Knowledge Base — Dedicated ESG Frameworks & Metrics Lookup
// Covers: GRI, TCFD, SASB, CSRD/ESRS, IFRS S1/S2, SEC Climate Rules, CDP,
//         SBTi, BRSR (India), EU Taxonomy
// ─────────────────────────────────────────────────────────────────────────────

export interface ESGStandard {
	id: string;
	domain: "ESG";
	framework: string;
	title: string;
	summary: string;
	keyRequirements: string[];
	relatedStandards?: string[];
}

export interface ESGMetric {
	name: string;
	formula: string;
	unit: string;
	interpretation: string;
	benchmark?: string;
}

export interface ESGLookupInput {
	query: string;
	framework?: string; // e.g. 'GRI', 'TCFD', 'CSRD', 'SASB'
}

export interface ESGLookupOutput {
	query: string;
	standards: ESGStandard[];
	metrics: ESGMetric[];
	guidance: string;
}

// ── ESG Standards ───────────────────────────────────────────────────────────

const ESG_STANDARDS: ESGStandard[] = [
	{
		id: "GRI-302",
		domain: "ESG",
		framework: "GRI",
		title: "GRI 302 – Energy",
		summary: "Disclosure standard for energy consumption, intensity, and reduction.",
		keyRequirements: [
			"Disclose fuel consumption from non-renewable and renewable sources.",
			"Report energy consumption within and outside the organization.",
			"Report energy intensity ratio (per revenue, product, or FTE).",
			"Disclose reductions in energy consumption and energy sold.",
			"Context: reporting period, base year, conversion factors used.",
		],
		relatedStandards: ["GRI-305", "TCFD-Metrics", "SASB-Energy"],
	},
	{
		id: "GRI-305",
		domain: "ESG",
		framework: "GRI",
		title: "GRI 305 – Emissions",
		summary:
			"Disclosure standard for Scope 1, 2, and 3 GHG emissions, intensity, and reduction targets.",
		keyRequirements: [
			"Scope 1: Direct GHG emissions (tCO₂e) from owned/controlled sources.",
			"Scope 2: Indirect from purchased energy — location-based and market-based.",
			"Scope 3: Upstream and downstream value chain emissions.",
			"GHG intensity ratio per unit revenue, production, or FTE.",
			"Biogenic CO₂ reported separately. SBTi-aligned targets disclosed.",
		],
		relatedStandards: ["GRI-302", "TCFD-Metrics", "ESRS-E1", "SEC-Climate"],
	},
	{
		id: "GRI-303",
		domain: "ESG",
		framework: "GRI",
		title: "GRI 303 – Water and Effluents",
		summary: "Water withdrawal, consumption, discharge, and water-stress area management.",
		keyRequirements: [
			"Disclose total water withdrawal by source and water-stress area.",
			"Report water consumption and changes in water storage.",
			"Disclose water discharge by quality, destination, and treatment.",
			"Identify operations in high or extremely high baseline water stress.",
			"Explain approach to managing significant water-related impacts.",
		],
		relatedStandards: ["GRI-304", "ESRS-E3", "SASB-Water"],
	},
	{
		id: "GRI-401",
		domain: "ESG",
		framework: "GRI",
		title: "GRI 401 – Employment",
		summary: "Disclosure for new hires, turnover, and employee benefits.",
		keyRequirements: [
			"Report new hires and turnover by age, gender, and region.",
			"Disclose benefits for full-time vs. part-time/temporary employees.",
			"Include parental leave return and retention rates.",
		],
		relatedStandards: ["GRI-403", "GRI-405", "ESRS-S1"],
	},
	{
		id: "GRI-403",
		domain: "ESG",
		framework: "GRI",
		title: "GRI 403 – Occupational Health and Safety",
		summary: "OHS management, hazard identification, and injury statistics.",
		keyRequirements: [
			"OHS management system scope and worker participation.",
			"Hazard identification, risk assessment, and incident investigation.",
			"TRIR, fatalities, high-consequence injuries reported.",
			"Distinguish employees vs. contractors in all disclosures.",
		],
		relatedStandards: ["GRI-401", "ISO-45001", "ESRS-S1"],
	},
	{
		id: "GRI-405",
		domain: "ESG",
		framework: "GRI",
		title: "GRI 405 – Diversity and Equal Opportunity",
		summary: "Diversity of governance bodies/employees and equal remuneration.",
		keyRequirements: [
			"Governance body composition by gender, age, minority indicators.",
			"Employee diversity per category by gender, age, and minority.",
			"Ratio of women-to-men remuneration by employee category.",
			"Pay equity analysis methodology context.",
		],
		relatedStandards: ["GRI-406", "ESRS-S1", "SASB-HC"],
	},
	{
		id: "TCFD-Framework",
		domain: "ESG",
		framework: "TCFD",
		title: "TCFD – Climate-related Financial Disclosures",
		summary:
			"Voluntary climate disclosure framework: Governance, Strategy, Risk Management, Metrics & Targets.",
		keyRequirements: [
			"Governance: Board oversight; management role in climate risk.",
			"Strategy: Climate risks/opportunities; scenario analysis (1.5°C, 2°C).",
			"Risk Management: Process for identifying, assessing, managing climate risks.",
			"Metrics & Targets: Scope 1/2/3 GHG; climate targets; progress.",
			"Transition risks: policy, technology, market, reputational.",
			"Physical risks: acute (extreme weather) and chronic (sea level, temperature).",
		],
		relatedStandards: ["GRI-305", "ESRS-E1", "SEC-Climate", "IFRS-S2"],
	},
	{
		id: "IFRS-S1",
		domain: "ESG",
		framework: "IFRS-S",
		title: "IFRS S1 – General Sustainability Disclosure Requirements",
		summary:
			"Overall requirements for disclosing material sustainability-related risks and opportunities.",
		keyRequirements: [
			"Disclose material sustainability risks/opportunities affecting enterprise value.",
			"Four core elements: Governance, Strategy, Risk Management, Metrics & Targets.",
			"Connected information: link sustainability to financial statements.",
			"Comparative period required. Effective from 1 January 2024.",
		],
		relatedStandards: ["IFRS-S2", "TCFD-Framework", "GRI-305"],
	},
	{
		id: "IFRS-S2",
		domain: "ESG",
		framework: "IFRS-S",
		title: "IFRS S2 – Climate-related Disclosures",
		summary:
			"Climate-related risks/opportunities disclosure with scenario analysis and cross-industry metrics.",
		keyRequirements: [
			"Climate-related risks (physical and transition) and opportunities.",
			"Scenario analysis: at least two scenarios including ≤1.5°C.",
			"Cross-industry metrics: Scope 1/2/3 GHG; carbon offsets; internal carbon price.",
			"SASB industry-based metrics integrated. Financed emissions for FIs.",
		],
		relatedStandards: ["IFRS-S1", "TCFD-Framework", "GRI-305", "SEC-Climate"],
	},
	{
		id: "ESRS-E1",
		domain: "ESG",
		framework: "CSRD/ESRS",
		title: "ESRS E1 – Climate Change (EU CSRD)",
		summary:
			"EU mandatory disclosure: climate adaptation, mitigation, energy, GHG with double materiality.",
		keyRequirements: [
			"Double materiality: financial and impact materiality.",
			"Transition plan aligned with Paris Agreement / 1.5°C.",
			"Absolute Scope 1, 2, and 3 GHG emissions.",
			"GHG intensity and reduction targets with 5-year milestones.",
			"Potential financial effects of climate risks/opportunities.",
			"Mandatory for large EU companies from FY2024.",
		],
		relatedStandards: ["TCFD-Framework", "GRI-305", "IFRS-S2"],
	},
	{
		id: "SEC-Climate",
		domain: "ESG",
		framework: "SEC",
		title: "SEC Climate Disclosure Rules (2024)",
		summary:
			"US SEC mandatory climate risk, governance, strategy, and GHG disclosures in annual reports.",
		keyRequirements: [
			"Material climate-related risks and their impacts.",
			"Board and management oversight of climate risks.",
			"Integration with enterprise risk management.",
			"Scope 1 and 2 GHG (large accelerated filers, phased-in).",
			"Scope 3 only if material or included in targets.",
			"Financial statement effects: severe weather and carbon offsets > 1%.",
		],
		relatedStandards: ["TCFD-Framework", "IFRS-S2", "GRI-305"],
	},
	{
		id: "SASB-Standards",
		domain: "ESG",
		framework: "SASB",
		title: "SASB – Industry-Specific Sustainability Accounting",
		summary: "77 industry standards identifying financially material ESG topics and metrics.",
		keyRequirements: [
			"Identify applicable industry standard from 77 SASB standards.",
			"Report on financially material ESG topics for the industry.",
			"SASB metrics are quantitative and comparable across peers.",
			"Integrated into ISSB IFRS S2 as industry-based metrics.",
		],
		relatedStandards: ["IFRS-S2", "GRI-Standards", "TCFD-Framework"],
	},
	{
		id: "CDP-Climate",
		domain: "ESG",
		framework: "CDP",
		title: "CDP Climate Change Questionnaire",
		summary:
			"Annual disclosure questionnaire covering governance, risks/opportunities, business strategy, targets, and Scope 1/2/3 emissions. Scored A–D with public bands.",
		keyRequirements: [
			"Governance: board oversight, management responsibility, climate-linked incentives.",
			"Risks & opportunities: time horizons, transition + physical, financial impact ranges.",
			"Strategy: scenario analysis (≥2 scenarios incl. 1.5–2°C aligned), capex/opex tagged green/transition.",
			"Targets: absolute and intensity targets, base year, target year, SBTi validation status.",
			"Emissions: Scope 1 (mandatory), Scope 2 location- and market-based, Scope 3 by 15 GHG-Protocol categories.",
			"Verification: third-party assurance level for Scope 1/2/3 (limited or reasonable).",
			"Scoring: Disclosure → Awareness → Management → Leadership; A-list reserved for top performers.",
		],
		relatedStandards: ["TCFD-Framework", "GRI-305", "ISSB-S2", "SBTi"],
	},
	{
		id: "CDP-Water",
		domain: "ESG",
		framework: "CDP",
		title: "CDP Water Security Questionnaire",
		summary:
			"Annual water disclosure: withdrawal, consumption, discharge by source/destination, water-stress exposure, basin-level risks, targets.",
		keyRequirements: [
			"Volumetric water withdrawal, consumption, discharge — by source, destination, treatment.",
			"Water stress exposure using WRI Aqueduct or WWF Water Risk Filter.",
			"Basin-level risk assessment for facilities in water-stressed areas.",
			"Targets: withdrawal reduction, recycled-water %, basin-context targets.",
			"Supplier engagement on water (% of supply spend assessed).",
		],
		relatedStandards: ["GRI-303", "ESRS-E3", "SASB-Water"],
	},
	{
		id: "CDP-Forests",
		domain: "ESG",
		framework: "CDP",
		title: "CDP Forests Questionnaire",
		summary:
			"Disclosure on deforestation-linked commodities — timber, palm oil, soy, cattle, rubber, cocoa, coffee.",
		keyRequirements: [
			"Commodity volumes sourced and traceability to country/jurisdiction/farm.",
			"Deforestation- and conversion-free (DCF) cutoff date and policy.",
			"Verified-sustainable certifications (RSPO, FSC, RTRS, etc.) % of volume.",
			"Supplier engagement programs and grievance mechanisms.",
		],
		relatedStandards: ["TNFD", "ESRS-E4", "GRI-304"],
	},
	{
		id: "SBTi",
		domain: "ESG",
		framework: "SBTi",
		title: "Science Based Targets initiative — Net-Zero Standard",
		summary:
			"Validated science-aligned target framework. Near-term targets cover ≥5 yrs out (max 10), long-term net-zero by 2050. Aligned with 1.5°C pathway.",
		keyRequirements: [
			"Near-term Scope 1+2 absolute reduction: ≥4.2%/yr (1.5°C) or ≥2.5%/yr (well-below 2°C).",
			"Scope 3 target required if Scope 3 ≥40% of total. Coverage ≥67% of Scope 3.",
			"Long-term: ≥90% absolute reduction across Scope 1+2+3 by 2050.",
			"Residual emissions (≤10%) neutralized via permanent removals — not avoidance offsets.",
			"Sector-specific pathways: power, FLAG (forest, land, agriculture), buildings, transport, financial.",
			"Annual progress disclosure required; recalculation triggers (>5% change in inventory).",
		],
		relatedStandards: ["TCFD-Framework", "IFRS-S2", "GRI-305", "CDP-Climate"],
	},
	{
		id: "BRSR",
		domain: "ESG",
		framework: "BRSR",
		title: "Business Responsibility & Sustainability Reporting (SEBI, India)",
		summary:
			"Mandatory disclosure for top 1000 listed companies in India by market cap. Structured around 9 NGRBC principles. BRSR Core (subset) is reasonably assured.",
		keyRequirements: [
			"Section A: General disclosures — entity, subsidiaries, employee/worker breakdown by gender/category.",
			"Section B: Management & process — leadership statement, NGRBC governance, materiality, complaints.",
			"Section C — 9 Principles, each with Essential (mandatory) + Leadership (voluntary) indicators:",
			"  P1 Ethics & transparency (anti-corruption, compliance training).",
			"  P2 Sustainable products (R&D % on sustainable inputs, recycled material %, EPR).",
			"  P3 Employee well-being (wages, benefits, accessibility, retention by gender).",
			"  P4 Stakeholder engagement (vulnerable groups, grievance mechanisms).",
			"  P5 Human rights (training, complaints, value chain assessment).",
			"  P6 Environment (Scope 1/2/3, energy, water, waste, air emissions, biodiversity, EPR, climate risk).",
			"  P7 Public policy advocacy (positions taken, payments to trade bodies).",
			"  P8 Inclusive growth (CSR projects, social-impact assessments, beneficiary data).",
			"  P9 Customer responsibility (consumer complaints, data privacy, product information).",
			"BRSR Core: 9 KPIs (GHG intensity, water, waste, gender wage gap, complaints, etc.) need third-party reasonable assurance for top 250 by FY2025-26 cascading to top 1000.",
		],
		relatedStandards: ["GRI-Standards", "ESRS", "IFRS-S2", "SASB-Standards"],
	},
	{
		id: "EU-Taxonomy",
		domain: "ESG",
		framework: "EU-Taxonomy",
		title: "EU Taxonomy Regulation (2020/852)",
		summary:
			"Classification system identifying environmentally-sustainable economic activities. Required for CSRD reporters and EU financial market participants.",
		keyRequirements: [
			"Six environmental objectives: (1) Climate mitigation, (2) Climate adaptation, (3) Water & marine, (4) Circular economy, (5) Pollution prevention, (6) Biodiversity & ecosystems.",
			"Three tests: Substantial Contribution to ≥1 objective + Do No Significant Harm (DNSH) to others + Minimum Safeguards (OECD MNE Guidelines, UN Guiding Principles, ILO core conventions).",
			"Eligibility: activity is described in the Delegated Acts. Alignment: meets all three tests.",
			"Disclosures (Article 8): % of turnover, capex, opex from Taxonomy-aligned activities; broken down per objective.",
			"For non-financials: published in management report under CSRD.",
			"For financials: Green Asset Ratio (GAR) for banks, Taxonomy-aligned investments for insurers and asset managers.",
		],
		relatedStandards: ["ESRS-E1", "ESRS-E2", "ESRS-E3", "ESRS-E4", "ESRS-E5", "CSRD"],
	},
];

// ── ESG Metrics ─────────────────────────────────────────────────────────────

const ESG_METRICS: ESGMetric[] = [
	{
		name: "GHG Intensity (Scope 1+2)",
		formula: "(Scope 1 + Scope 2) ÷ Revenue",
		unit: "tCO₂e / $M revenue",
		interpretation: "Emissions per economic output. Lower is better.",
		benchmark: "Varies by industry; track year-over-year and vs. peers",
	},
	{
		name: "Scope 3 Intensity",
		formula: "Scope 3 Emissions ÷ Revenue",
		unit: "tCO₂e / $M revenue",
		interpretation: "Value chain emissions per revenue. Often 5–10× Scope 1+2.",
		benchmark: "SBTi requires Scope 3 alignment for most sectors",
	},
	{
		name: "Renewable Energy %",
		formula: "Renewable Energy ÷ Total Energy × 100",
		unit: "%",
		interpretation: "Share from renewable sources. RE100 target = 100%.",
		benchmark: "> 50% progressive; many targeting 100% by 2030",
	},
	{
		name: "Energy Intensity",
		formula: "Total Energy ÷ Revenue",
		unit: "MWh / $M revenue",
		interpretation: "Energy efficiency per output. Declining = improving.",
		benchmark: "Compare to sector average; year-over-year improvement",
	},
	{
		name: "Water Intensity",
		formula: "Water Withdrawal ÷ Revenue",
		unit: "m³ / $M revenue",
		interpretation: "Water use efficiency. Critical for water-stressed locations.",
		benchmark: "Varies by industry; benchmark against sector",
	},
	{
		name: "Waste Diversion Rate",
		formula: "Waste Diverted ÷ Total Waste × 100",
		unit: "%",
		interpretation: "Reused, recycled, or recovered waste %.",
		benchmark: "> 80% strong; targeting 90%+ or zero landfill",
	},
	{
		name: "TRIR",
		formula: "(Recordable Incidents × 200,000) ÷ Hours Worked",
		unit: "per 200k hours",
		interpretation: "Normalized injury rate. Lower is better.",
		benchmark: "Manufacturing avg ~2.0; leaders target < 0.5",
	},
	{
		name: "Employee Turnover Rate",
		formula: "Employees Left ÷ Avg Headcount × 100",
		unit: "%",
		interpretation: "Voluntary turnover indicates retention health.",
		benchmark: "< 10% low; 15–20% common across industries",
	},
	{
		name: "Gender Pay Gap",
		formula: "(Male Median − Female Median) ÷ Male Median × 100",
		unit: "%",
		interpretation: "Unadjusted pay gap. EU mandates < 5%.",
		benchmark: "Target = 0%; global average ~16%",
	},
	{
		name: "Board Diversity %",
		formula: "Diverse Directors ÷ Total Directors × 100",
		unit: "%",
		interpretation: "Gender/racial/ethnic representation on board.",
		benchmark: "ISS/Glass Lewis: ≥ 30% gender diversity",
	},
	{
		name: "Training Hours / Employee",
		formula: "Total Training Hours ÷ Total Employees",
		unit: "hours / FTE",
		interpretation: "Investment in employee development.",
		benchmark: "SHRM avg ~32 hrs/year; high-performers > 40 hrs",
	},
	{
		name: "SBTi-Aligned Annual Reduction Rate",
		formula: "(Base Year Emissions − Current Year Emissions) ÷ (Base Year × Years Elapsed) × 100",
		unit: "% / year",
		interpretation:
			"Annualized linear reduction rate vs. base year. SBTi requires ≥4.2%/yr for 1.5°C, ≥2.5%/yr for well-below 2°C.",
		benchmark: "1.5°C: ≥4.2% / yr; WB2°C: ≥2.5% / yr",
	},
	{
		name: "EU Taxonomy Turnover Alignment",
		formula: "Aligned Turnover ÷ Total Turnover × 100",
		unit: "%",
		interpretation:
			"Share of revenue from Taxonomy-aligned activities (substantial contribution + DNSH + minimum safeguards).",
		benchmark: "Sector-dependent; rising disclosure expectation under CSRD",
	},
	{
		name: "EU Taxonomy CapEx Alignment",
		formula: "Aligned CapEx ÷ Total CapEx × 100",
		unit: "%",
		interpretation:
			"Share of capital expenditure invested in Taxonomy-aligned activities or transition plans toward alignment.",
		benchmark: "Often the highest of T/Capex/Opex due to plan-based eligibility",
	},
	{
		name: "Green Asset Ratio (banks)",
		formula: "Taxonomy-Aligned Assets ÷ Total Covered Assets × 100",
		unit: "%",
		interpretation: "EU bank metric for share of green lending. Mandatory under Article 8 + CRR.",
		benchmark: "EU bank avg ~3% (2023); rising",
	},
	{
		name: "BRSR Scope 1+2 Intensity (Revenue)",
		formula: "(Scope 1 + Scope 2) ÷ Turnover (rupees, adjusted PPP)",
		unit: "tCO₂e / ₹ crore",
		interpretation:
			"BRSR Principle 6 essential indicator. Reasonably assured under BRSR Core for top 250 by FY26.",
		benchmark: "Sector- and currency-adjusted; compare within NIC industry code",
	},
	{
		name: "BRSR Female Employee % at Permanent Workforce",
		formula: "Permanent Female Employees ÷ Total Permanent Employees × 100",
		unit: "%",
		interpretation:
			"BRSR Principle 3 essential indicator. Reported separately for employees and workers.",
		benchmark: "India listed-co average ~22% (2024)",
	},
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function tokenize(text: string): string[] {
	return text
		.toLowerCase()
		.replace(/[^a-z0-9\s-]/g, " ")
		.split(/\s+/)
		.filter(Boolean);
}

function scoreStandard(std: ESGStandard, queryTokens: string[]): number {
	const haystack = tokenize(
		[std.id, std.framework, std.title, std.summary, ...std.keyRequirements].join(" ")
	);
	return queryTokens.reduce((score, token) => {
		const exact = haystack.filter((w) => w === token).length;
		const partial = haystack.filter((w) => w.includes(token)).length;
		return score + exact * 3 + partial;
	}, 0);
}

function scoreMetric(metric: ESGMetric, queryTokens: string[]): number {
	const haystack = tokenize(
		[
			metric.name,
			metric.formula,
			metric.unit,
			metric.interpretation,
			metric.benchmark ?? "",
		].join(" ")
	);
	return queryTokens.reduce((score, token) => {
		return score + (haystack.filter((w) => w.includes(token)).length > 0 ? 2 : 0);
	}, 0);
}

// ── Lookup Function ─────────────────────────────────────────────────────────

export function esgLookup(input: ESGLookupInput): ESGLookupOutput {
	const queryTokens = tokenize(input.query);

	let candidates = [...ESG_STANDARDS];
	if (input.framework) {
		const fw = input.framework.toLowerCase();
		candidates = candidates.filter((s) => s.framework.toLowerCase().includes(fw));
	}

	const standards = candidates
		.map((s) => ({ s, score: scoreStandard(s, queryTokens) }))
		.filter((x) => x.score > 0)
		.sort((a, b) => b.score - a.score)
		.slice(0, 4)
		.map((x) => x.s);

	const metrics = ESG_METRICS.map((m) => ({ m, score: scoreMetric(m, queryTokens) }))
		.filter((x) => x.score > 0)
		.sort((a, b) => b.score - a.score)
		.slice(0, 5)
		.map((x) => x.m);

	let guidance = "";
	if (standards.length === 0 && metrics.length === 0) {
		guidance = `No ESG standards matched "${input.query}". Try searching GRI, TCFD, SASB, CSRD/ESRS, or IFRS S1/S2.`;
	} else if (standards.length > 0) {
		guidance = `Found ${standards.length} standard(s). Key: ${standards[0].keyRequirements[0]}`;
	}
	if (metrics.length > 0) {
		guidance += ` ${metrics.length} metric(s) identified.`;
	}

	return { query: input.query, standards, metrics, guidance };
}

export { ESG_STANDARDS, ESG_METRICS };
